import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete,
    Button,
    IconButton,
    ResponsiveDialog,
    Stack,
    styled,
    Text,
    TextInput,
    Tooltip,
} from 'shell/components';
import { CourseImportType } from 'utils/constants';

const SelectCourseDialog = ({ open, importType, createCourseLoading, options, onSave, onClose, errors = '' }) => {
    const { t } = useTranslation('course');
    const [course, setCourse] = useState(null);
    const [virtualizedOptions, setVirtualizedOptions] = useState([]);

    const idTooltipText =
        process.env.REACT_APP_KIND === 'D2L'
            ? t(
                  'course-lobby.course-id-tooltip-d2l',
                  "To find your Course ID, go to your course homepage and check your browser's URL, and look right after “\u2060/\u2060home\u2060/\u2060”."
              )
            : t(
                  'course-lobby.course-id-tooltip-canvas',
                  "To find your Course ID, go to your course homepage and check your browser's URL, and look right after “\u2060/\u2060course\u2060/\u2060”."
              );

    const textMap = {
        [CourseImportType.NAME]: {
            title: t('course-lobby.select-course-import', 'Select a Course to Import'),
            subHeading: null,
            tooltip: null,
            label: t('course-lobby.course-name', 'Course Name'),
            placeholder: t('common:ui.select', 'Select'),
        },
        [CourseImportType.ID]: {
            title: t('course-lobby.import-using-course-id', 'Import Using Course ID'),
            subHeading: t('course-lobby.import-course-id-subheading', 'Where can I find my course ID?'),
            tooltip: idTooltipText,
            label: t('course-lobby.course-id', 'Course ID'),
            placeholder: t('course-lobby.enter-course-id', 'Enter Course ID'),
        },
    };

    const currentText = textMap[importType] || textMap.name;

    useEffect(() => {
        setCourse(null);
    }, [open]);

    useEffect(() => {
        if (options.length > 0) {
            const virtualizedOptions = options.map((option) => ({
                ...option,
                key: option.id,
                value: importType === CourseImportType.ID ? option.id : option.name,
            }));        
            setVirtualizedOptions(virtualizedOptions);
        }
    }, [importType, options]);

    return (
        <CustomResponsiveDialog aria-labelledby={'dialog-title'} open={open} onClose={onClose}>
            <Stack spacing={8}>
                <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                    <Stack gap={3}>
                        <Text id={'dialog-title'} component={'h1'} variant={'h6'} weight={'semibold'}>
                            {currentText.title}
                        </Text>
                        {(currentText.subHeading || currentText.tooltip) && (
                            <Stack direction={'row'} alignItems={'center'} gap={3}>
                                {!!currentText.subHeading && <Text color='light'>{currentText.subHeading}</Text>}
                                {!!currentText.tooltip && <Tooltip content={currentText.tooltip} type={'info'} />}
                            </Stack>
                        )}
                    </Stack>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
                {importType === CourseImportType.NAME ? (
                    <CustomAutocomplete
                        required
                        label={currentText.label}
                        options={virtualizedOptions}
                        placeholder={currentText.placeholder}                       
                        virtualized
                        onChange={(_, selectedItem) => {
                            setCourse(selectedItem);                          
                        }}
                    />
                ) : (
                    <TextInput
                        required
                        label={currentText.label}
                        placeholder={currentText.placeholder}
                        value={course ?? ''}
                        onChange={(e) => {
                            setCourse(e.target.value);
                        }}
                        error={Boolean(errors)}
                        helperText={errors}
                    />
                )}              
                <Button
                    sx={{ alignSelf: 'flex-end' }}
                    loading={createCourseLoading}
                    onClick={() => onSave(course)}
                    disabled={!course || (typeof course === 'string' && !course.trim())}
                >
                    {t('common:controls.import', 'Import')}
                </Button>
            </Stack>
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiDialog-paper > .MuiBox-root': {
            width: '600px',
        },
    };
});
const CustomAutocomplete = styled(Autocomplete)(() => {
    return {
        width: '530px',
        '& + .MuiAutocomplete-popper > .MuiAutocomplete-paper': {
            width: '530px !important',
        },
    };
});

export default SelectCourseDialog;
